import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { SUPABASE_URL } from "@/constants/supabase";
import { EmailPageLayout } from "@/components/email/EmailPageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertCircle,
  CheckCircle2,
  Download,
  FileUp,
  Filter,
  Loader2,
  Plus,
  RefreshCcw,
  Search,
  Trash2,
  Upload,
  UserPlus,
  Users,
} from "lucide-react";

// Define interfaces for our data
interface Preferences {
  documentRequests: boolean;
  informationRequests: boolean;
  serviceUpdates: boolean;
  marketing: boolean;
}

interface Subscriber {
  id: string;
  email: string;
  name: string;
  status: string;
  subscriptionDate: string;
  source: string;
  lastEngagement: string;
  preferences: Preferences;
}

interface Segment {
  id: number;
  name: string;
  description: string;
  count: number;
  criteria: string;
}

export default function ListManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [logSearchTerm, setLogSearchTerm] = useState("");
  const [logTypeFilter, setLogTypeFilter] = useState("all");
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isCreateSegmentDialogOpen, setIsCreateSegmentDialogOpen] =
    useState(false);
  const [isAddSubscriberDialogOpen, setIsAddSubscriberDialogOpen] =
    useState(false);

  // State for real data
  const [subscribersList, setSubscribersList] = useState<Subscriber[]>([]);
  const [segmentsList, setSegmentsList] = useState<Segment[]>([]);
  const [emailLogs, setEmailLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // State for add subscriber form
  const [newSubscriberEmail, setNewSubscriberEmail] = useState("");
  const [newSubscriberName, setNewSubscriberName] = useState("");
  const [newSubscriberSource, setNewSubscriberSource] = useState("direct");
  const [newSubscriberPrefs, setNewSubscriberPrefs] = useState({
    documentRequests: true,
    informationRequests: true,
    serviceUpdates: true,
    marketing: false,
  });
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [addingSubscriber, setAddingSubscriber] = useState(false);

  // Function to fetch data from database
  const fetchData = async () => {
    setLoading(true);

    try {
      // Fetch email subscribers from the database
      const { data: emailSubscribers, error: subscribersError } = await supabase
        .from("email_subscribers")
        .select("*")
        .order("created_at", { ascending: false });

      if (subscribersError) {
        console.error("Error fetching email subscribers:", subscribersError);
        throw subscribersError;
      }

      // Fetch email preferences for subscribers
      const { data: emailPreferences, error: preferencesError } = await supabase
        .from("email_preferences")
        .select("*");

      if (preferencesError) {
        console.error("Error fetching email preferences:", preferencesError);
      }

      // Fetch email logs to get engagement information
      const { data: emailLogs, error: emailLogsError } = await supabase
        .from("email_logs")
        .select("*")
        .order("timestamp", { ascending: false });

      if (emailLogsError) {
        console.error("Error fetching email logs:", emailLogsError);
      }

      // Fetch bounces to identify bounced subscribers
      const { data: bounces, error: bouncesError } = await supabase
        .from("email_bounces")
        .select("*");

      if (bouncesError) {
        console.error("Error fetching bounces:", bouncesError);
      }

      // Fetch complaints to identify complained subscribers
      const { data: complaints, error: complaintsError } = await supabase
        .from("email_complaints")
        .select("*");

      if (complaintsError) {
        console.error("Error fetching complaints:", complaintsError);
      }

      // Create a map of subscribers with their preferences
      const subscribersMap = new Map<string, Subscriber>();

      // Process email subscribers
      emailSubscribers?.forEach((subscriber) => {
        // Find preferences for this subscriber
        const preferences = emailPreferences?.find(
          (pref) => pref.subscriber_id === subscriber.id
        );

        // Find the most recent engagement from email logs
        const subscriberLogs = emailLogs?.filter(
          (log) => log.recipient === subscriber.email
        );
        const lastEngagement = subscriberLogs?.length
          ? subscriberLogs.reduce((latest, log) => {
              return new Date(log.timestamp) > new Date(latest.timestamp)
                ? log
                : latest;
            }, subscriberLogs[0]).timestamp
          : subscriber.created_at;

        // Extract name from email if not available
        const name = subscriber.email.split("@")[0];

        // Determine status - if unsubscribed_all is true, override status to 'unsubscribed'
        let status = subscriber.status || "active";
        if (preferences?.unsubscribed_all) {
          status = "unsubscribed";
        }

        subscribersMap.set(subscriber.email, {
          id: subscriber.id,
          email: subscriber.email,
          name: name,
          status: status,
          subscriptionDate: subscriber.created_at,
          source: subscriber.source || "direct",
          lastEngagement: lastEngagement,
          preferences: {
            documentRequests: preferences?.document_requests ?? true,
            informationRequests: preferences?.information_requests ?? true,
            serviceUpdates: preferences?.service_updates ?? true,
            marketing: preferences?.marketing_communications ?? false,
          },
        });
      });

      // If we don't have any subscribers from the email_subscribers table,
      // fall back to creating them from email logs (for backward compatibility)
      if (subscribersMap.size === 0 && emailLogs?.length) {
        // Process email logs to extract subscriber information
        emailLogs.forEach((log) => {
          if (!subscribersMap.has(log.recipient)) {
            // Extract name from recipient if available (format: "Name <email@example.com>")
            let name = "";
            let email = log.recipient;

            const match = log.recipient.match(/(.+)\s<(.+)>/i);
            if (match) {
              name = match[1];
              email = match[2];
            }

            // Generate a unique ID for this subscriber
            const uniqueId = `log-${Date.now()}-${Math.random()
              .toString(36)
              .substring(2, 9)}`;

            subscribersMap.set(email, {
              id: uniqueId,
              email: email,
              name: name || email.split("@")[0], // Use part before @ if no name available
              status: "active",
              subscriptionDate: log.timestamp,
              source: "direct",
              lastEngagement: log.timestamp,
              preferences: {
                documentRequests: true,
                informationRequests: true,
                serviceUpdates: true,
                marketing: false,
              },
            });
          } else {
            // Update last engagement if this log is more recent
            const subscriber = subscribersMap.get(log.recipient)!;
            const logDate = new Date(log.timestamp);
            const lastEngagementDate = new Date(subscriber.lastEngagement);

            if (logDate > lastEngagementDate) {
              subscriber.lastEngagement = log.timestamp;
            }
          }
        });
      }

      // Mark bounced subscribers
      bounces?.forEach((bounce) => {
        if (subscribersMap.has(bounce.email)) {
          const subscriber = subscribersMap.get(bounce.email)!;
          subscriber.status = "bounced";
        }
      });

      // Mark complained subscribers
      complaints?.forEach((complaint) => {
        if (subscribersMap.has(complaint.email)) {
          const subscriber = subscribersMap.get(complaint.email)!;
          subscriber.status = "complained";
        }
      });

      // Convert map to array
      const subscribers = Array.from(subscribersMap.values());

      // Create segments based on the subscriber data
      const segments: Segment[] = [
        {
          id: 1,
          name: "All Subscribers",
          description: "All subscribers in the system",
          count: subscribers.length,
          criteria: "all",
        },
        {
          id: 2,
          name: "Active Subscribers",
          description: "Subscribers with active status",
          count: subscribers.filter((s) => s.status === "active").length,
          criteria: "status = 'active'",
        },
        {
          id: 3,
          name: "Unsubscribed",
          description: "Subscribers who have unsubscribed",
          count: subscribers.filter((s) => s.status === "unsubscribed").length,
          criteria: "status = 'unsubscribed'",
        },
        {
          id: 4,
          name: "Bounced Emails",
          description: "Subscribers with bounced emails",
          count: subscribers.filter((s) => s.status === "bounced").length,
          criteria: "status = 'bounced'",
        },
        {
          id: 5,
          name: "Complained Subscribers",
          description: "Subscribers who have complained",
          count: subscribers.filter((s) => s.status === "complained").length,
          criteria: "status = 'complained'",
        },
        {
          id: 6,
          name: "Recent Subscribers",
          description: "Subscribers added in the last 30 days",
          count: subscribers.filter((s) => {
            const thirtyDaysAgo = new Date();
            thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
            return new Date(s.subscriptionDate) >= thirtyDaysAgo;
          }).length,
          criteria: "subscriptionDate > (now - 30 days)",
        },
      ];

      // Update state with the fetched data
      setSubscribersList(subscribers);
      setSegmentsList(segments);
      setEmailLogs(emailLogs || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchData();
  }, []);

  // Filter subscribers based on search term and status filter
  const filteredSubscribers = subscribersList.filter((subscriber) => {
    const matchesSearch =
      subscriber.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subscriber.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || subscriber.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Filter email logs based on search term and type filter
  const filteredEmailLogs = emailLogs.filter((log) => {
    const matchesSearch =
      log.recipient?.toLowerCase().includes(logSearchTerm.toLowerCase()) ||
      log.subject?.toLowerCase().includes(logSearchTerm.toLowerCase());
    const matchesType =
      logTypeFilter === "all" || log.email_type === logTypeFilter;
    return matchesSearch && matchesType;
  });

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedSubscribers(filteredSubscribers.map((s) => s.id));
    } else {
      setSelectedSubscribers([]);
    }
  };

  const handleSelectSubscriber = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedSubscribers([...selectedSubscribers, id]);
    } else {
      setSelectedSubscribers(
        selectedSubscribers.filter((subId) => subId !== id)
      );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "warning";
      case "unsubscribed":
        return "secondary";
      case "bounced":
        return "destructive";
      case "complained":
        return "destructive";
      default:
        return "secondary";
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <EmailPageLayout
      title="Email List Management"
      description="Manage subscribers, segments, and email preferences"
      action={
        <Button
          variant="outline"
          size="sm"
          className="h-8 px-3 text-xs bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/40"
          onClick={fetchData}
          disabled={loading}
        >
          <RefreshCcw
            className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`}
          />
          {loading ? "Refreshing..." : "Refresh Data"}
        </Button>
      }
    >
      <div className="space-y-6">
        <Tabs defaultValue="subscribers">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="subscribers">
              <Users className="h-4 w-4 mr-2" />
              Subscribers
            </TabsTrigger>
            <TabsTrigger value="segments">
              <Filter className="h-4 w-4 mr-2" />
              Segments
            </TabsTrigger>
            <TabsTrigger value="logs">
              <RefreshCcw className="h-4 w-4 mr-2" />
              Email Logs
            </TabsTrigger>
          </TabsList>

          <TabsContent value="subscribers" className="space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full md:w-[300px]"
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                    <SelectItem value="bounced">Bounced</SelectItem>
                    <SelectItem value="complained">Complained</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsImportDialogOpen(true)}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Import
                </Button>

                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>

                <Button
                  size="sm"
                  onClick={() => setIsAddSubscriberDialogOpen(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Subscriber
                </Button>
              </div>
            </div>

            {selectedSubscribers.length > 0 && (
              <div className="bg-muted p-2 rounded-md flex items-center justify-between">
                <span className="text-sm">
                  {selectedSubscribers.length} subscribers selected
                </span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    Add to Segment
                  </Button>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Remove
                  </Button>
                </div>
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={
                            selectedSubscribers.length ===
                              filteredSubscribers.length &&
                            filteredSubscribers.length > 0
                          }
                          onCheckedChange={handleSelectAll}
                        />
                      </TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Subscription Date</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Last Engagement</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      // Show loading skeletons
                      Array.from({ length: 5 }).map((_, index) => (
                        <TableRow
                          key={`loading-${index}`}
                          className="opacity-70"
                        >
                          <TableCell>
                            <div className="h-4 w-4 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-40 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-32 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 w-20 bg-muted rounded-full animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-16 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="h-8 w-16 bg-muted rounded animate-pulse ml-auto"></div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : filteredSubscribers.length > 0 ? (
                      filteredSubscribers.map((subscriber) => (
                        <TableRow key={subscriber.id}>
                          <TableCell>
                            <Checkbox
                              checked={selectedSubscribers.includes(
                                subscriber.id
                              )}
                              onCheckedChange={(checked) =>
                                handleSelectSubscriber(subscriber.id, !!checked)
                              }
                            />
                          </TableCell>
                          <TableCell className="font-medium">
                            {subscriber.email}
                          </TableCell>
                          <TableCell>{subscriber.name}</TableCell>
                          <TableCell>
                            <Badge variant={getStatusColor(subscriber.status)}>
                              {subscriber.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {formatDate(subscriber.subscriptionDate)}
                          </TableCell>
                          <TableCell>{subscriber.source}</TableCell>
                          <TableCell>
                            {formatDate(subscriber.lastEngagement)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-4">
                          No subscribers found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading subscriber data...
                    </div>
                  ) : (
                    `Showing ${filteredSubscribers.length} of ${subscribersList.length} subscribers`
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>List Hygiene</AlertTitle>
              <AlertDescription>
                Regularly clean your email list by removing inactive subscribers
                and addressing bounces to maintain good deliverability.
              </AlertDescription>
            </Alert>
          </TabsContent>

          <TabsContent value="segments" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium">Email Segments</h3>
              <Button onClick={() => setIsCreateSegmentDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Segment
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {loading ? (
                // Show loading skeletons
                Array.from({ length: 3 }).map((_, index) => (
                  <Card key={`loading-${index}`} className="opacity-70">
                    <CardHeader>
                      <div className="h-6 w-3/4 bg-muted rounded animate-pulse mb-2"></div>
                      <div className="h-4 w-full bg-muted rounded animate-pulse"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between mb-2">
                        <div className="h-4 w-1/3 bg-muted rounded animate-pulse"></div>
                        <div className="h-6 w-12 bg-muted rounded-full animate-pulse"></div>
                      </div>
                      <div className="mt-2">
                        <div className="h-4 w-1/4 bg-muted rounded animate-pulse mb-1"></div>
                        <div className="h-8 w-full bg-muted rounded animate-pulse"></div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-end gap-2">
                      <div className="h-8 w-16 bg-muted rounded animate-pulse"></div>
                      <div className="h-8 w-32 bg-muted rounded animate-pulse"></div>
                    </CardFooter>
                  </Card>
                ))
              ) : segmentsList.length > 0 ? (
                segmentsList.map((segment) => (
                  <Card key={segment.id}>
                    <CardHeader>
                      <CardTitle>{segment.name}</CardTitle>
                      <CardDescription>{segment.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          Subscribers:
                        </span>
                        <Badge variant="secondary">{segment.count}</Badge>
                      </div>
                      <div className="mt-2">
                        <div className="text-xs text-muted-foreground mb-1">
                          Criteria:
                        </div>
                        <code className="text-xs bg-muted p-1 rounded">
                          {segment.criteria}
                        </code>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-end gap-2">
                      <Button variant="outline" size="sm">
                        Edit
                      </Button>
                      <Button variant="outline" size="sm">
                        View Subscribers
                      </Button>
                    </CardFooter>
                  </Card>
                ))
              ) : (
                <Card className="col-span-full">
                  <CardContent className="flex flex-col items-center justify-center py-8">
                    <div className="text-center text-muted-foreground">
                      <Filter className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="text-lg font-medium mb-2">
                        No segments found
                      </h3>
                      <p className="mb-4">
                        Create your first segment to organize your subscribers.
                      </p>
                      <Button
                        onClick={() => setIsCreateSegmentDialogOpen(true)}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Create Segment
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="logs" className="space-y-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-2 w-full md:w-auto">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by recipient or subject..."
                  className="w-full md:w-[300px]"
                  value={logSearchTerm}
                  onChange={(e) => setLogSearchTerm(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Select value={logTypeFilter} onValueChange={setLogTypeFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="test">Test</SelectItem>
                    <SelectItem value="transactional">Transactional</SelectItem>
                    <SelectItem value="marketing">Marketing</SelectItem>
                    <SelectItem value="notification">Notification</SelectItem>
                  </SelectContent>
                </Select>

                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  Export Logs
                </Button>
              </div>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>Recipient</TableHead>
                      <TableHead>Subject</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      // Show loading skeletons
                      Array.from({ length: 5 }).map((_, index) => (
                        <TableRow
                          key={`loading-log-${index}`}
                          className="opacity-70"
                        >
                          <TableCell>
                            <div className="h-4 w-24 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-40 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-48 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 w-20 bg-muted rounded-full animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-6 w-16 bg-muted rounded-full animate-pulse"></div>
                          </TableCell>
                          <TableCell>
                            <div className="h-4 w-32 bg-muted rounded animate-pulse"></div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="h-8 w-16 bg-muted rounded animate-pulse ml-auto"></div>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : // Show actual email logs
                    filteredEmailLogs.length > 0 ? (
                      // Map through email logs
                      filteredEmailLogs.slice(0, 10).map((log, index) => (
                        <TableRow key={`email-log-${index}`}>
                          <TableCell>
                            {new Date(log.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>{log.recipient}</TableCell>
                          <TableCell>{log.subject}</TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                log.email_type === "test"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : log.email_type === "marketing"
                                  ? "bg-green-50 text-green-700 border-green-200"
                                  : log.email_type === "notification"
                                  ? "bg-purple-50 text-purple-700 border-purple-200"
                                  : "bg-blue-50 text-blue-700 border-blue-200"
                              }
                            >
                              {log.email_type || "transactional"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                log.status === "sent" ||
                                log.status === "delivered"
                                  ? "success"
                                  : log.status === "pending"
                                  ? "warning"
                                  : log.status === "failed"
                                  ? "destructive"
                                  : "secondary"
                              }
                            >
                              {log.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{log.template_name}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm">
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-4">
                          No email logs found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
              <CardFooter className="flex items-center justify-between border-t p-4">
                <div className="text-sm text-muted-foreground">
                  {loading ? (
                    <div className="flex items-center">
                      <Loader2 className="h-3 w-3 mr-2 animate-spin" />
                      Loading email logs...
                    </div>
                  ) : (
                    `Showing ${
                      filteredEmailLogs.length > 10
                        ? "10"
                        : filteredEmailLogs.length
                    } of ${filteredEmailLogs.length} email logs`
                  )}
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious href="#" />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink href="#" isActive>
                        1
                      </PaginationLink>
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationNext href="#" />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </CardFooter>
            </Card>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Email Monitoring</AlertTitle>
              <AlertDescription>
                Track email delivery, opens, and engagement to improve your
                communication strategy.
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </div>

      {/* Import Subscribers Dialog */}
      <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Import Subscribers</DialogTitle>
            <DialogDescription>
              Upload a CSV file with subscriber data. The file should include
              email, name, and subscription date columns.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-md p-6 text-center">
              <FileUp className="h-8 w-8 mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">
                Drag and drop your CSV file here, or click to browse
              </p>
              <Button variant="outline" size="sm" className="mt-2">
                Browse Files
              </Button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Import Options</Label>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox id="skip-header" />
                  <Label htmlFor="skip-header">Skip header row</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="update-existing" />
                  <Label htmlFor="update-existing">
                    Update existing subscribers
                  </Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox id="send-confirmation" />
                  <Label htmlFor="send-confirmation">
                    Send confirmation email to new subscribers
                  </Label>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsImportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Import Subscribers</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Segment Dialog */}
      <Dialog
        open={isCreateSegmentDialogOpen}
        onOpenChange={setIsCreateSegmentDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Segment</DialogTitle>
            <DialogDescription>
              Create a new segment by defining criteria to filter subscribers.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="segment-name">Segment Name</Label>
              <Input id="segment-name" placeholder="e.g., Active Clients" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment-description">Description</Label>
              <Textarea
                id="segment-description"
                placeholder="Describe the purpose of this segment"
              />
            </div>

            <div className="space-y-2">
              <Label>Segment Criteria</Label>

              <div className="space-y-2 border rounded-md p-4">
                <div className="grid grid-cols-3 gap-2">
                  <Select defaultValue="status">
                    <SelectTrigger>
                      <SelectValue placeholder="Field" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="status">Status</SelectItem>
                      <SelectItem value="source">Source</SelectItem>
                      <SelectItem value="subscriptionDate">
                        Subscription Date
                      </SelectItem>
                      <SelectItem value="lastEngagement">
                        Last Engagement
                      </SelectItem>
                      <SelectItem value="preferences">Preferences</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select defaultValue="equals">
                    <SelectTrigger>
                      <SelectValue placeholder="Condition" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="notEquals">Not Equals</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="greaterThan">Greater Than</SelectItem>
                      <SelectItem value="lessThan">Less Than</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select defaultValue="active">
                    <SelectTrigger>
                      <SelectValue placeholder="Value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="unsubscribed">Unsubscribed</SelectItem>
                      <SelectItem value="bounced">Bounced</SelectItem>
                      <SelectItem value="complained">Complained</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button variant="outline" size="sm" className="w-full mt-2">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Condition
                </Button>
              </div>
            </div>

            <div className="bg-muted rounded-md p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Preview</span>
                <Button variant="outline" size="sm">
                  <RefreshCcw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              </div>
              <div className="mt-2">
                <div className="text-sm">
                  Matching subscribers: <strong>7</strong>
                </div>
                <Progress value={70} className="mt-2" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsCreateSegmentDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Create Segment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Subscriber Dialog */}
      <Dialog
        open={isAddSubscriberDialogOpen}
        onOpenChange={setIsAddSubscriberDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Subscriber</DialogTitle>
            <DialogDescription>
              Add a new subscriber to your email list.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subscriber-email">Email Address *</Label>
              <Input
                id="subscriber-email"
                placeholder="email@example.com"
                type="email"
                value={newSubscriberEmail}
                onChange={(e) => setNewSubscriberEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subscriber-name">Full Name</Label>
              <Input
                id="subscriber-name"
                placeholder="John Smith"
                value={newSubscriberName}
                onChange={(e) => setNewSubscriberName(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Email Preferences</Label>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pref-document"
                    checked={newSubscriberPrefs.documentRequests}
                    onCheckedChange={(checked) =>
                      setNewSubscriberPrefs({
                        ...newSubscriberPrefs,
                        documentRequests: !!checked,
                      })
                    }
                  />
                  <Label htmlFor="pref-document">Document Requests</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pref-information"
                    checked={newSubscriberPrefs.informationRequests}
                    onCheckedChange={(checked) =>
                      setNewSubscriberPrefs({
                        ...newSubscriberPrefs,
                        informationRequests: !!checked,
                      })
                    }
                  />
                  <Label htmlFor="pref-information">Information Requests</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pref-service"
                    checked={newSubscriberPrefs.serviceUpdates}
                    onCheckedChange={(checked) =>
                      setNewSubscriberPrefs({
                        ...newSubscriberPrefs,
                        serviceUpdates: !!checked,
                      })
                    }
                  />
                  <Label htmlFor="pref-service">Service Updates</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="pref-marketing"
                    checked={newSubscriberPrefs.marketing}
                    onCheckedChange={(checked) =>
                      setNewSubscriberPrefs({
                        ...newSubscriberPrefs,
                        marketing: !!checked,
                      })
                    }
                  />
                  <Label htmlFor="pref-marketing">
                    Marketing Communications
                  </Label>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="subscriber-source">Subscription Source</Label>
              <Select
                value={newSubscriberSource}
                onValueChange={setNewSubscriberSource}
              >
                <SelectTrigger id="subscriber-source">
                  <SelectValue placeholder="Select source" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">Direct Entry</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="import">Import</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="send-welcome"
                  checked={sendWelcomeEmail}
                  onCheckedChange={(checked) => setSendWelcomeEmail(!!checked)}
                />
                <Label htmlFor="send-welcome">Send welcome email</Label>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddSubscriberDialogOpen(false);
                // Reset form
                setNewSubscriberEmail("");
                setNewSubscriberName("");
                setNewSubscriberSource("direct");
                setNewSubscriberPrefs({
                  documentRequests: true,
                  informationRequests: true,
                  serviceUpdates: true,
                  marketing: false,
                });
                setSendWelcomeEmail(true);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={async () => {
                if (!newSubscriberEmail) {
                  alert("Email address is required");
                  return;
                }

                setAddingSubscriber(true);

                try {
                  console.log("Adding subscriber:", newSubscriberEmail);

                  // 1. Add subscriber to email_subscribers table
                  const { data: subscriber, error: subscriberError } =
                    await supabase
                      .from("email_subscribers")
                      .insert({
                        email: newSubscriberEmail,
                        status: "active",
                        source: newSubscriberSource,
                      })
                      .select()
                      .single();

                  console.log("Subscriber result:", subscriber);
                  console.log("Subscriber error:", subscriberError);

                  if (subscriberError) {
                    throw subscriberError;
                  }

                  // 2. Add preferences to email_preferences table
                  const { data: preferences, error: preferencesError } =
                    await supabase
                      .from("email_preferences")
                      .insert({
                        subscriber_id: subscriber.id,
                        document_requests: newSubscriberPrefs.documentRequests,
                        information_requests:
                          newSubscriberPrefs.informationRequests,
                        service_updates: newSubscriberPrefs.serviceUpdates,
                        marketing_communications: newSubscriberPrefs.marketing,
                        unsubscribed_all: false,
                      })
                      .select();

                  console.log("Preferences result:", preferences);
                  console.log("Preferences error:", preferencesError);

                  if (preferencesError) {
                    throw preferencesError;
                  }

                  // 3. Send welcome email if selected
                  if (sendWelcomeEmail) {
                    // TODO: Implement welcome email sending
                    console.log("Sending welcome email to", newSubscriberEmail);
                  }

                  // 4. Refresh the data
                  await fetchData();

                  // 5. Close the dialog and reset form
                  setIsAddSubscriberDialogOpen(false);
                  setNewSubscriberEmail("");
                  setNewSubscriberName("");
                  setNewSubscriberSource("direct");
                  setNewSubscriberPrefs({
                    documentRequests: true,
                    informationRequests: true,
                    serviceUpdates: true,
                    marketing: false,
                  });
                  setSendWelcomeEmail(true);

                  // 6. Show success message
                  alert("Subscriber added successfully!");
                } catch (error) {
                  console.error("Error adding subscriber:", error);
                  alert(`Error adding subscriber: ${error.message || error}`);
                } finally {
                  setAddingSubscriber(false);
                }
              }}
              disabled={addingSubscriber || !newSubscriberEmail}
            >
              {addingSubscriber ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Add Subscriber
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EmailPageLayout>
  );
}
